import React, { useCallback, useContext, useState } from "react";
import { Modal, styled } from "@mui/material";
import { usePatientCase } from "./usePatientCase";
import {
  ClinicModeContext,
  useDiagnosticAgentType,
} from "../../context/diagnosticAgent";
import { saveCase } from "../../saveCase/savedCases";
import PatientCaseShare from "./PatientCaseShare";
import { useShareLinks } from "../../saveCase/useShareLinks";
import { shareCaseModalZIndex } from "fe-common/ui/stacking";
import { useCreateSharedCase } from "./hooks/useCreateSharedCase";
import { useDiagnosticNavigation } from "./useDiagnosticNavigation";
import { ActionButton } from "fe-common/ui/buttons/actionButton";
import { ShareCaseDialog } from "./common/ShareCaseDialog";
import { isFakeProduction, isProduction } from "fe-common/utils/envGetter";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import ShareIcon from "../../assets/shareIcon.svg";

//region [[ Styles ]]

const ShareCaseModal = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: shareCaseModalZIndex,
});
//endregion [[ Styles ]]

//region [[ Props ]]
export interface ShareCaseMenuItemProps {
  button?: React.ReactElement;
  className?: string;
  onClose?: () => void;
  onlyIcon?: boolean;
}
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const ShareCaseMenuItem = ({
  onClose,
  onlyIcon,
  ...props
}: ShareCaseMenuItemProps) => {
  const agentType = useDiagnosticAgentType()[0];
  const createSharedCase = useCreateSharedCase(agentType);
  const { patientCase } = usePatientCase();
  const { inWizard } = useDiagnosticNavigation();
  const [shareId, setShareId] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const { shareExternal, shareExternalSupported } = useShareLinks();

  const clinicMode = useContext(ClinicModeContext);

  const onShareClick = useCallback(
    async (e) => {
      const patientCaseId = await createSharedCase();
      if (patientCaseId) {
        await saveCase(patientCase, patientCaseId);
        evt({
          category: CATEGORY.AGENT,
          action: ACTION.SHARE_CASE,
          label: patientCaseId,
        });
        if (shareExternalSupported) {
          shareExternal(patientCaseId);
        } else {
          setShareId(patientCaseId);
          setShareDialogOpen(true);
        }
      }
    },
    [shareExternalSupported, shareExternal, patientCase, createSharedCase],
  );

  const injectButton = useCallback(
    (el: React.ReactElement, other: object = {}) =>
      React.cloneElement(el, {
        ...(other || {}),
        onClick: onShareClick,
        disabled: inWizard,
      }),
    [onShareClick, inWizard],
  );

  const DefaultShareCaseButton = () => (
    <ActionButton
      className={props.className}
      onClick={onShareClick}
      label={"Share Case"}
      icon={<img src={ShareIcon} alt={"Share Case"} />}
      iconOnly={onlyIcon}
    />
  );

  const handleClosed = useCallback(() => {
    setShareDialogOpen(false);
    if (onClose) onClose();
  }, [setShareDialogOpen, onClose]);

  const ShareCaseDialogView = () =>
    isProduction() || isFakeProduction() ? (
      <ShareCaseDialog
        shareId={shareId}
        isOpen={shareDialogOpen}
        onClose={handleClosed}
      />
    ) : (
      <ShareCaseModal open={shareDialogOpen}>
        <PatientCaseShare shareId={shareId} onClose={handleClosed} />
      </ShareCaseModal>
    );

  if (clinicMode) return null;

  return (
    <>
      {props.button ? injectButton(props.button) : DefaultShareCaseButton()}
      <ShareCaseDialogView />
    </>
  );
};
